import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { CatalogSong } from '../../../../redux/api/fymAPI.types';
import { catalogSongMessages } from './catalogSongMessages';
import { MAX_MASTER_MB } from './masterFile';
import { useMasterUpload } from './useMasterUpload';
import { useMasterPick } from './useMasterPick';
import { MasterDropZone } from './MasterDropZone';
import { MasterProgress } from './MasterProgress';
import { PendingMasterConfirm } from './PendingMasterConfirm';
import { MasterFileWithPlayer } from './player/MasterFileWithPlayer';
import { masterAudio } from './player/masterAudio';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song files; each part uses its own slice of it */
import styles from './SongFiles.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MasterUploadRowProps {
  accountId: number;
  itemUuid: string;
  master: CatalogSong['master'];
  /** False on the song page: its bar holds the player, so the master line shows no player. */
  showPlayer?: boolean;
  /** The demo artist: no drop zone and no Replace (design song-page f7). */
  readOnly?: boolean;
  /** One more sentence of the Replace warning (song page c6: the hand-set times go). */
  replaceNote?: string;
  /** Tells the page that an upload runs, for its leave guard (f4). */
  onUploadingChange?: (isUploading: boolean) => void;
}

/**
 * The master of the song files sheet (designs f2, release-kit o2): the file with its length, BPM
 * and hook, the player, Download and Replace; or the drop zone when the row has no master.
 * Replace warns first that finished videos keep the old master. The rights box shows on the first upload of the account (g1).
 */
const MasterUploadRowComponent: React.FC<MasterUploadRowProps> = ({
  accountId,
  itemUuid,
  master,
  showPlayer = true,
  readOnly = false,
  replaceNote,
  onUploadingChange,
}) => {
  const intl = useIntl();
  const [isReplacing, setIsReplacing] = useState(false);
  const stopReplacing = useCallback(() => setIsReplacing(false), []);
  const startReplacing = useCallback(() => {
    // The old master stops before the new one replaces it.
    masterAudio.stop(itemUuid);
    setIsReplacing(true);
  }, [itemUuid]);
  const upload = useMasterUpload({ accountId, itemUuid });
  const pick = useMasterPick({ upload, onSaved: stopReplacing });
  const isBusy = pick.isUploading || upload.finishingName !== undefined;
  useEffect(() => {
    onUploadingChange?.(isBusy);
  }, [isBusy, onUploadingChange]);
  useEffect(() => () => onUploadingChange?.(false), [onUploadingChange]);

  let body: React.ReactNode;
  if (pick.isUploading) {
    body = (
      <MasterProgress
        title={pick.uploadingName ?? ''}
        progress={upload.progress}
        onStop={upload.canStop ? upload.cancel : undefined}
      />
    );
  } else if (upload.finishingName !== undefined) {
    // The sheet closed and opened again while the last call of an upload runs.
    body = (
      <MasterProgress
        title={upload.finishingName}
        progress={null}
        note={intl.formatMessage(catalogSongMessages.finishing)}
      />
    );
  } else if (pick.pending !== undefined) {
    body = (
      <PendingMasterConfirm
        file={pick.pending}
        rightsChecked={pick.rightsChecked}
        onRightsChange={pick.setRightsChecked}
        onUpload={pick.confirm}
        onCancel={pick.cancel}
      />
    );
  } else if (master === null && readOnly) {
    body = null;
  } else if (master === null || isReplacing) {
    body = (
      <>
        {isReplacing && (
          <div className="d-flex align-items-center gap-3">
            <span className={styles.footNote}>
              {intl.formatMessage(catalogSongMessages.replaceWarning)}
              {replaceNote !== undefined && ` ${replaceNote}`}
            </span>
            <button type="button" className={styles.linkButton} onClick={stopReplacing}>
              {intl.formatMessage(catalogSongMessages.cancel)}
            </button>
          </div>
        )}
        <MasterDropZone
          title={intl.formatMessage(isReplacing ? catalogSongMessages.replaceConfirm : catalogSongMessages.dropTitle)}
          hint={intl.formatMessage(catalogSongMessages.dropHint, { max: MAX_MASTER_MB })}
          onFile={pick.pick}
        />
      </>
    );
  } else {
    body = (
      <MasterFileWithPlayer
        accountId={accountId}
        itemUuid={itemUuid}
        master={master}
        showPlayer={showPlayer}
        onReplace={readOnly ? undefined : startReplacing}
      />
    );
  }

  return (
    <div className={styles.field}>
      <span className={styles.label}>{intl.formatMessage(catalogSongMessages.master)}</span>
      {body}
      {upload.error !== null && (
        <Alert color="danger" className="mb-0">
          {upload.error}
        </Alert>
      )}
    </div>
  );
};

export const MasterUploadRow = TypedMemo(MasterUploadRowComponent);
